/** The map behind the fleet page: a dot per Chromebook, its accuracy as a ring, and the school's areas drawn on top. */
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';
import type { Geofence, LocatedDevice } from '../lib/device-location';

interface Props {
  devices: LocatedDevice[];
  geofences?: Geofence[];
  tiles: { tileUrl: string; attribution: string };
  selectedId?: string | null;
  onSelect?(deviceId: string): void;
  className?: string;
}

const COLOURS = { missing: '#dc2626', quiet: '#d97706', reporting: '#0b84a3' };

const colourOf = (device: LocatedDevice) =>
  device.missingAt && !device.recoveredAt ? COLOURS.missing : device.status === 'REPORTING' ? COLOURS.reporting : COLOURS.quiet;

export function FleetMap({ devices, geofences = [], tiles, selectedId, onSelect, className }: Props) {
  const holder = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markers = useRef(L.layerGroup());
  const areas = useRef(L.layerGroup());

  // The map itself is made once; its layers are redrawn as the fleet changes.
  useEffect(() => {
    if (!holder.current || map.current) return;
    const created = L.map(holder.current, { attributionControl: true, zoomControl: true }).setView([39.5, -98.35], 4);
    // Providers that restrict a key by site need the tile requests to say where they came from.
    if (tiles.tileUrl) {
      L.tileLayer(tiles.tileUrl, { attribution: tiles.attribution, maxZoom: 19, referrerPolicy: 'strict-origin-when-cross-origin' }).addTo(created);
    }
    areas.current.addTo(created);
    markers.current.addTo(created);
    map.current = created;
    return () => {
      created.remove();
      map.current = null;
    };
  }, [tiles.tileUrl, tiles.attribution]);

  useEffect(() => {
    const drawn = areas.current;
    drawn.clearLayers();
    for (const fence of geofences) {
      const colour = fence.kind === 'RESTRICTED' ? '#dc2626' : fence.kind === 'ALLOWED' ? '#16a34a' : '#0b84a3';
      const style = { color: colour, weight: 2, fillOpacity: 0.06, dashArray: fence.kind === 'CAMPUS' ? undefined : '6 4' };
      if (fence.polygon?.length) drawn.addLayer(L.polygon(fence.polygon, style).bindTooltip(fence.name));
      else if (fence.centerLat !== null && fence.centerLng !== null && fence.radiusM)
        drawn.addLayer(L.circle([fence.centerLat, fence.centerLng], { radius: fence.radiusM, ...style }).bindTooltip(fence.name));
    }
  }, [geofences]);

  useEffect(() => {
    const layer = markers.current;
    layer.clearLayers();
    const located = devices.filter((device) => device.lastLatitude !== null && device.lastLongitude !== null);
    for (const device of located) {
      const at: L.LatLngExpression = [device.lastLatitude!, device.lastLongitude!];
      const colour = colourOf(device);
      if (device.lastAccuracyM) {
        layer.addLayer(L.circle(at, { radius: device.lastAccuracyM, color: colour, weight: 1, opacity: 0.4, fillOpacity: 0.08, interactive: false }));
      }
      const dot = L.circleMarker(at, {
        radius: device.id === selectedId ? 10 : 7,
        color: '#ffffff',
        weight: 2,
        fillColor: colour,
        fillOpacity: 1,
      }).bindTooltip(`${device.asset?.assetTag ?? device.serialNumber}${device.room ? ` · ${device.room.name}` : ''}`);
      dot.on('click', () => onSelect?.(device.id));
      layer.addLayer(dot);
    }
    // Fit the view the first time there's something to show, and whenever the selection moves off screen.
    const current = map.current;
    if (!current || !located.length) return;
    const bounds = L.latLngBounds(located.map((device) => [device.lastLatitude!, device.lastLongitude!] as L.LatLngExpression));
    const selected = located.find((device) => device.id === selectedId);
    if (selected) current.setView([selected.lastLatitude!, selected.lastLongitude!], Math.max(current.getZoom(), 16));
    else current.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
  }, [devices, selectedId, onSelect]);

  return <div ref={holder} className={className ?? 'h-[28rem] w-full rounded-lg border border-slate-200'} />;
}
